import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, unref as _unref, createElementVNode as _createElementVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "qtl-note" }
const _hoisted_2 = ["title", "aria-label"]
const _hoisted_3 = ["title", "aria-label"]
const _hoisted_4 = { class: "qtl-note__template-menu" }

import { computed, nextTick, ref, watch } from "vue";
import { i18n } from "@/i18n";
import { AnalyticsV2ElementType } from "@/models/etc/types";
import type { CellDataSimple, TableDataCell } from "@/models/interfaces/entity-settings/querytable/querytable";
import { rootStore } from "@/store";
import { generalStore } from "@/store/general";
import { storagePositionsStore } from "@/store/storage/storage-positions";
import { storageMovementsStore } from "@/store/storage/storage-movements";
import type { StorageNoteTemplateDto } from "@/models/interfaces/storage/storage-models";
import { formatLocalDateTimeAsPlus00 } from "@/utils/auxfunc";
import MostaContextMenu from "@/components/common/basic/MostaContextMenu.vue";
import NativeInput from "@/components/common/basic/NativeInput.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'NotesCellElementLite',
  props: {
    element: {},
    entityId: {},
    creatorId: {}
  },
  setup(__props: any) {

const props = __props;

const t = (k: string) => i18n.global.t(k);
const rootstore = rootStore();
const genstore = generalStore();
const storagePos = storagePositionsStore();
const storageMove = storageMovementsStore();

const inputRef = ref<any>(null);
const templateBtnRef = ref<HTMLElement | null>(null);

const isCommentEditing = ref(false);
const textContentInner = ref("");
const originalComment = ref("");
// Должен быть ref, а не reactive: aux-selector отдаёт выбор через переназначение
// v-model (update:modelValue с новым массивом). reactive-const переназначить нельзя,
// поэтому watcher не срабатывал бы и вставка шаблона не работала.
const comment_templates = ref<StorageNoteTemplateDto[]>([]);
const lastCommentCaretPosition = ref(0);
let _blockCaretUpdate = false;
const tids = ref<number[]>([]);

const menuOpen = ref(false);
const menuPos = ref<{ top: string; left: string }>({ top: "0px", left: "0px" });

// Значение комментария собираем из Data (строковые значения, переносы как есть).
// Пустым считаем только null/undefined; 0 не превращаем в пустую строку.
const textContent = computed(() =>
  props.element.Data
    .map((cell) => {
      const v = (cell as CellDataSimple).Value;
      return v === null || v === undefined ? "" : String(v);
    })
    .join(" "),
);

textContentInner.value = textContent.value;
updateLastCaretPosition();

const isMovementNote = computed(
  () => genstore?.selectedItem?.DestType === AnalyticsV2ElementType.StorageItemMovement,
);

const hasWA = computed(() => genstore?.accessRights?.WriteAccess || false);

// Движенческий комментарий редактирует только его автор.
const canEditMovementCommentByAuthor = computed(() => {
  if (!isMovementNote.value) return true;
  const creatorId = Number(props.creatorId ?? 0);
  const changerId = Number(rootstore?.oldAPIUID ?? 0);
  return creatorId > 0 && changerId > 0 && creatorId === changerId;
});

const canShowCommentEditButton = computed(() => hasWA.value && canEditMovementCommentByAuthor.value);

function onCaretPositionChange(val: number) {
  if (val >= 0 && !_blockCaretUpdate) {
    lastCommentCaretPosition.value = +val;
  }
}

// Вставка текста выбранного шаблона в позицию каретки.
watch(comment_templates, () => {
  if (!comment_templates.value.length) return;

  const templateText = (comment_templates.value[0] as StorageNoteTemplateDto).Text;
  tids.value.push((comment_templates.value[0] as StorageNoteTemplateDto).Id);

  const original = textContentInner.value || "";

  let c_pos = lastCommentCaretPosition.value;
  if (c_pos < 0) c_pos = 0;
  else if (c_pos > original.length) c_pos = original.length;

  _blockCaretUpdate = true;
  textContentInner.value = original.slice(0, c_pos) + templateText + original.slice(c_pos);
  lastCommentCaretPosition.value = c_pos + templateText.length;
  comment_templates.value = [];

  menuOpen.value = false;
  nextTick(() => {
    _blockCaretUpdate = false;
  });
});

function updateLastCaretPosition() {
  if (lastCommentCaretPosition.value < textContentInner.value.length) {
    lastCommentCaretPosition.value = textContentInner.value.length;
  }
}

function addCommentTemplate(t_name: string) {
  if (t_name && t_name.length && textContentInner.value && textContentInner.value.length) {
    if (genstore.selectedItem?.DestType === AnalyticsV2ElementType.StorageItem) {
      storagePos.createNoteTemplate({ Name: t_name, Text: textContentInner.value });
    } else if (genstore.selectedItem?.DestType === AnalyticsV2ElementType.StorageItemMovement) {
      storageMove.createMovementNoteTemplate({ Name: t_name, Text: textContentInner.value });
    }
  }
}

function deleteCommentTemplate() {}

function onCommentInput(newname: string) {
  textContentInner.value = newname;
}

function onCommentKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") cancelCommentEditing();
}

function onCommentBlur() {
  if (!isCommentEditing.value) return;
  _blockCaretUpdate = true;
  setTimeout(() => {
    _blockCaretUpdate = false;
  }, 0);
}

function onNativeInputClick() {
  if (!isCommentEditing.value) return;
  setTimeout(() => {
    const field = inputRef.value?.$refs?.editable_field as HTMLElement | null;
    if (!field) return;
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const pre = range.cloneRange();
      pre.selectNodeContents(field);
      pre.setEnd(range.endContainer, range.endOffset);
      lastCommentCaretPosition.value = pre.toString().length;
    }
  }, 0);
}

function openTemplateMenu(ev: MouseEvent) {
  const rect = templateBtnRef.value?.getBoundingClientRect();
  if (rect) {
    menuPos.value = { top: `${rect.top}px`, left: `${rect.right + 4}px` };
  } else {
    menuPos.value = { top: `${ev.clientY}px`, left: `${ev.clientX}px` };
  }
  menuOpen.value = true;
  // Позицию уточняем после рендера: если меню не влезает справа (ячейка у правого
  // края), открываем слева от кнопки; в любом случае держим меню в пределах экрана.
  // nextTick-правка проходит до отрисовки браузером, поэтому мигания нет.
  nextTick(() => adjustTemplateMenuPosition());
}

function adjustTemplateMenuPosition() {
  const MARGIN = 8;
  const inner = document.querySelector(".qtl-note__template-menu");
  const menuEl = inner?.closest(".mosta-context-menu") as HTMLElement | null;
  const rect = templateBtnRef.value?.getBoundingClientRect();
  if (!menuEl) return;

  const menuW = menuEl.offsetWidth || 200;
  const menuH = menuEl.offsetHeight || 0;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let left = rect ? rect.right + 4 : parseFloat(menuPos.value.left) || 0;
  let top = rect ? rect.top : parseFloat(menuPos.value.top) || 0;

  // Справа не помещается - разворачиваем меню влево от кнопки.
  if (rect && left + menuW > vw - MARGIN) {
    left = rect.left - menuW - 4;
  }

  // Финальный клэмп по обоим краям экрана.
  left = Math.max(MARGIN, Math.min(left, vw - menuW - MARGIN));
  top = Math.max(MARGIN, Math.min(top, vh - menuH - MARGIN));

  menuPos.value = { top: `${top}px`, left: `${left}px` };
}

function startCommentEditing() {
  if (!canShowCommentEditButton.value) return;
  originalComment.value = isMovementNote.value ? "" : textContentInner.value;
  if (isMovementNote.value) textContentInner.value = "";
  isCommentEditing.value = true;
  nextTick(() => {
    if (inputRef.value?.startEdit) inputRef.value.startEdit();
  });
}

function cancelCommentEditing() {
  textContentInner.value = originalComment.value;
  isCommentEditing.value = false;
  tids.value.splice(0);
}

async function saveComment() {
  const newText = textContentInner.value;
  if (genstore.selectedItem?.DestType === AnalyticsV2ElementType.StorageItem) {
    await storagePos.editStorageItemNote({
      ElementId: props.entityId,
      Value: newText,
      UsedNoteTemplateIds: tids.value,
    });
  } else if (genstore.selectedItem?.DestType === AnalyticsV2ElementType.StorageItemMovement) {
    const res = await storageMove.appendMovementNote({
      ElementId: props.entityId,
      Value: newText,
      UsedNoteTemplateIds: tids.value,
      IssuedAt: formatLocalDateTimeAsPlus00(),
    });
    textContentInner.value = res?.data?.Value || textContent.value;
  }
  tids.value.splice(0);
  isCommentEditing.value = false;
}

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_aux_selector = _resolveComponent("aux-selector")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(NativeInput, {
      ref_key: "inputRef",
      ref: inputRef,
      class: "qtl-note__input",
      "break-anywhere": true,
      "show-border": isCommentEditing.value,
      "model-value": textContentInner.value,
      "edit-on-click": isCommentEditing.value,
      "disabled-field": !hasWA.value || !isCommentEditing.value,
      "onUpdate:caretPosition": onCaretPositionChange,
      "onUpdate:modelValue": onCommentInput,
      onKeydown: onCommentKeydown,
      onClick: onNativeInputClick,
      onBlur: onCommentBlur
    }, null, 8, ["show-border", "model-value", "edit-on-click", "disabled-field"]),
    (isCommentEditing.value)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          ref_key: "templateBtnRef",
          ref: templateBtnRef,
          type: "button",
          class: "qtl-note__btn qtl-note__btn--template",
          title: t('templates'),
          "aria-label": t('templates'),
          onClick: _withModifiers(openTemplateMenu, ["stop"])
        }, [
          _createVNode(AppIcon, {
            name: "comment",
            width: "16px",
            color: "#979797",
            "hover-color": "#499259",
            cursor: "pointer"
          })
        ], 8, _hoisted_2))
      : _createCommentVNode("", true),
    (canShowCommentEditButton.value)
      ? (_openBlock(), _createElementBlock("button", {
          key: 1,
          type: "button",
          class: _normalizeClass(["qtl-note__btn", isCommentEditing.value ? 'qtl-note__btn--apply' : 'qtl-note__btn--edit']),
          title: isCommentEditing.value ? t('apply_changes') : t('edit_label'),
          "aria-label": isCommentEditing.value ? t('apply_changes') : t('edit_label'),
          onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (isCommentEditing.value ? saveComment() : startCommentEditing()), ["stop"]))
        }, [
          _createVNode(_component_q_icon, {
            name: isCommentEditing.value ? 'done' : 'edit',
            size: "18px"
          }, null, 8, ["name"])
        ], 10, _hoisted_3))
      : _createCommentVNode("", true),
    _createVNode(MostaContextMenu, {
      visible: menuOpen.value,
      position: menuPos.value,
      onClose: _cache[4] || (_cache[4] = ($event: any) => (menuOpen.value = false))
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_component_aux_selector, {
            "model-type": _unref(genstore).selectedItem?.DestType === 7 ? 24 : 25,
            "input-index": 0,
            "allow-multiple": false,
            "allow-free-input": true,
            modelValue: comment_templates.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((comment_templates).value = $event)),
            onDeleteItem: deleteCommentTemplate,
            "onUpdate:caretPosition": onCaretPositionChange,
            onInputValue: _cache[2] || (_cache[2] = (val) => addCommentTemplate(val)),
            onSelectorClosed: _cache[3] || (_cache[3] = ($event: any) => (menuOpen.value = false))
          }, null, 8, ["model-type", "modelValue"])
        ])
      ]),
      _: 1
    }, 8, ["visible", "position"])
  ]))
}
}

})